import { definePropType } from '@types-vue/props'
import { useHistoryStore, useTimelineStore, useTrackStore } from '@ve-pro/store'
import { Position } from '@ve-pro/types/index.type'
import type { SegmentCollection } from '@ve-pro/types/track.type'
import { getSegmentRect } from './track/track-rect'
import { computed, ExtractPropTypes } from 'vue'
import { SegmentType } from '@ve-pro/enums/segment'
import {
  getTransition,
  getTransitionById,
  isTransition,
} from '@ve-pro/store/tracks/segment/segment-transition'
import { getSegmentPosition } from './track/track-coord'
import { getTrackSegmentHeight } from './track/track-height'
import {
  getGroupBySegment,
  isSegmentGroupChild,
} from '@ve-pro/store/tracks/segment/segment-group'
import { isSegmentVideoImage } from '@ve-pro/store/tracks/resolve-segment'
import { addSegmentFragment } from '@ve-pro/store/tracks/add-segment-fragment'
import { updateMainTrack } from '@ve-pro/store/tracks/update-main-track'
import {
  isTemplateSegment,
  tryGetTemplateSegmentGroup,
} from '@ve-pro/store/tracks/tracks-actions'
import { framesToDuration } from '@ve-pro/store/tracks/frame'
import { isSegmentSubtitleGroup } from '@ve-pro/store/tracks/segment/segment-subtitle'

export { getConflictSegment } from '@ve-pro/store/tracks/add-segment-action'

export const SegmentComponentProps = {
  data: {
    type: definePropType<SegmentCollection>(Object),
    required: true,
    default: () => ({}), // 增加这个：为了解决下面类型的提示为可选的问题
  },
  active: {
    type: definePropType<boolean>(Boolean),
    default: false,
  },
  extra: {
    type: definePropType<{ movable: boolean; position: Position } | null>([
      Object,
      null,
    ]),
    default: null,
  },
  // 是否强制缩略图展示loading状态
  forceSegmentVideoFrameLoading: {
    type: definePropType<boolean>(Boolean),
    default: false,
  },
  disabled: {
    type: definePropType<boolean>(Boolean),
    default: false,
  },
  position: definePropType<Partial<Position>>(Object),
  enableEditButton: Boolean,
  // 补帧帧数（填充最后一帧的画面）
  fillFrames: {
    type: Number,
    default: 0,
  },
  // 普通项目，是否允许布局模版的主视频坑位的UI
  enableTemplateMainVideoStyle: Boolean,
}

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export function useState(
  props: ExtractPropTypes<typeof SegmentComponentProps>,
) {
  const timelineStore = useTimelineStore()
  const trackStore = useTrackStore()

  const duration = computed(() => {
    if (isSegmentSubtitleGroup(props.data)) {
      return trackStore.totalTime
    }

    if (
      isTemplateSegment(props.data) &&
      props.data.type === SegmentType.Video &&
      props.data.is_main_video
    ) {
      return trackStore.totalTime
    }

    const isText = props.data.type === SegmentType.Text
    const isImage = isSegmentVideoImage(props.data)
    // 文本子元件和图片子元件
    if (isSegmentGroupChild(props.data) && (isText || isImage)) {
      const groupSegment = getGroupBySegment(props.data)
      return groupSegment?.duration ?? props.data.duration
    } else if (isTransition(props.data) && trackStore.isCutting(props.data)) {
      return props.data.cuttingDuration
    }
    return props.data.duration
  })
  const filledFramesDuration = computed(() => {
    return duration.value + framesToDuration(props.fillFrames)
  })
  const style = computed(() => {
    let pos: Position | undefined
    if (!props.extra || !props.extra.movable) {
      pos = {
        ...getSegmentPosition(tryGetTemplateSegmentGroup(props.data)),
        ...(props.position ?? {}),
      }
    } else {
      if (isTransition(props.data)) {
        const beforeSegmentRect = getSegmentRect(props.data.before, false)
        const transitionSegmentRect = getSegmentRect(props.data)
        const w =
          beforeSegmentRect.width -
          (props.data.overlap ? 0 : transitionSegmentRect.width / 2)

        pos = { ...props.extra.position, x: props.extra.position.x + w }
      } else {
        pos = props.extra.position
      }
    }

    const behindTransitionWidth = computed(() =>
      getBehindTransitionWidth(props.data, props.active, trackStore.dragging),
    )

    /**
     * 拖拽中的视频元件，如果有后转场，需要处理一下视频元件的宽度（视频宽度+转场宽度=总宽度），不能重叠
     */
    const width =
      timelineStore.timeToWidth(duration.value) +
      timelineStore.framesToWidth(props.fillFrames) //  - behindTransitionWidth.value

    const height = getTrackSegmentHeight(props.data)

    const styles: any = {
      'touch-action': 'none',
      top: `${pos?.y ?? 0}px`,
      left:
        isSegmentGroupChild(props.data) && !isTemplateSegment(props.data)
          ? 0
          : `${pos?.x ?? 0}px`,
      width: `${width}px`,
      height: `${height}px`,
    }

    if (trackStore.dragging && props.active) {
      styles['z-index'] = '3'
    }
    if (props.data.type === SegmentType.Transition) {
      styles['z-index'] = '4'
    }

    return styles
  })
  const paddingLeft = computed(() => getPaddingLeft(props.data, props.active))
  const paddingRight = computed(() => getPaddingRight(props.data, props.active))

  const innerBoxStyle = computed(() => {
    const width = `calc(100% - ${paddingLeft.value + paddingRight.value}px)`
    const s: any = {
      width,
      marginLeft: `${paddingLeft.value}px`,
    }

    if (props.active) {
      s['z-index'] = '1'
      s['position'] = 'relative'
    }
    return s
  })

  const segmentHdStyle = computed(() => {
    const padding = 8

    if (props.data.type === SegmentType.Video) {
      const { before_transition_id } = props.data
      const transition = before_transition_id
        ? getTransitionById(before_transition_id)
        : null

      if (transition) {
        const transitionDuration = trackStore.isCutting(transition)
          ? transition.cuttingDuration
          : transition.duration
        const w = timelineStore.timeToWidth(transitionDuration)
        const left = w / 2

        return {
          paddingLeft: `${left + padding}px`,
        }
      }
    }

    return {}
  })

  const activeFillFrameBoxStyle = computed(() => {
    if (props.fillFrames === 0) {
      return {
        display: 'none',
      }
    }

    return {
      width: timelineStore.framesToWidth(props.fillFrames) + 'px',
    }
  })

  return {
    duration,
    filledFramesDuration,
    style,
    segmentHdStyle,
    innerBoxStyle,
    activeFillFrameBoxStyle,
  }
}

export function getBehindTransitionWidth(
  segment: SegmentCollection,
  active: boolean,
  dragging: boolean,
): number {
  const timelineStore = useTimelineStore()
  if (dragging && active && segment.type === SegmentType.Video) {
    const { behind } = getTransition(segment)
    if (behind) {
      const w = timelineStore.timeToWidth(behind.duration)
      return behind.overlap ? w : w / 2
    }
  }
  return 0
}

export function getPaddingRight(
  segment: SegmentCollection,
  active: boolean,
): number {
  const timelineStore = useTimelineStore()

  let value = 0
  if (!active && segment.type === SegmentType.Video) {
    const { behind_transition_id } = segment
    const transition = behind_transition_id
      ? getTransitionById(behind_transition_id)
      : null

    if (transition && transition.overlap) {
      const w = timelineStore.timeToWidth(transition.duration)
      value = w / 2
    }
  }
  return value
}

export function getPaddingLeft(
  segment: SegmentCollection,
  active: boolean,
): number {
  const timelineStore = useTimelineStore()

  let value = 0
  if (!active && segment.type === SegmentType.Video) {
    const { before_transition_id } = segment
    const transition = before_transition_id
      ? getTransitionById(before_transition_id)
      : null

    if (transition && transition.overlap) {
      const w = timelineStore.timeToWidth(transition.duration)
      value = w / 2
    }
  }
  return value
}

export function createEmptyFragment(): ReturnType<typeof addSegmentFragment> {
  const historyStore = useHistoryStore()
  const trackStore = useTrackStore()

  return addSegmentFragment([]).then((s) => {
    historyStore.push()
    s.start_time = trackStore.totalTime
    updateMainTrack(true, {
      mainTrack: trackStore.defaultExcludeFragmentMainTrack ?? undefined,
    })
    trackStore.activeSegmentFragment = s

    return s
  })
}
